import React, { useState } from 'react';
import { ArrowDownTrayIcon, ArrowUpOnSquareIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { useToast } from '../../contexts/ToastContext';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { displayName } from '../../utils/brand';

const DISMISS_KEY = 'ct-install-banner-dismissed';
/** Ennyi napig nem kérdezünk rá újra, ha a felhasználó elhalasztotta. */
const DISMISS_DAYS = 14;

/** Telefonon fut-e (nem asztali böngészőben, ahol a telepítés nem ennyire hasznos). */
const isPhone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(pointer: coarse)').matches || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent));

const recentlyDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
};

interface InstallAppBannerProps {
  className?: string;
}

/**
 * A főoldalon: ha az app még nincs a telefon kezdőképernyőjén, egy
 * „Letöltés” gomb. Androidon/Chrome-ban egy kattintással telepít, iPhone-on
 * megmutatja a két lépést. Telepítve, asztali gépen, vagy elhalasztva nem látszik.
 */
const InstallAppBanner: React.FC<InstallAppBannerProps> = ({ className = '' }) => {
  const { mode, install, busy } = useInstallPrompt();
  const { settings } = useClubSettings();
  const { showToast } = useToast();
  const [hidden, setHidden] = useState(recentlyDismissed);
  const [showIosSteps, setShowIosSteps] = useState(false);

  if (hidden || !isPhone() || (mode !== 'prompt' && mode !== 'ios-manual')) return null;

  const appName = displayName(settings?.clubName);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Privát módban nem tudjuk megjegyezni - ennél a megnyitásnál akkor is eltűnik
    }
    setHidden(true);
  };

  const handleDownload = async () => {
    if (mode === 'ios-manual') {
      setShowIosSteps((v) => !v);
      return;
    }
    const outcome = await install();
    if (outcome === 'accepted') showToast('Az app telepítve.');
    else if (outcome === 'unavailable') showToast('A telepítés most nem érhető el ebben a böngészőben.', 'info');
  };

  return (
    <div className={`rounded-2xl bg-primary-600 p-4 text-white shadow-sm ${className}`}>
      <div className="flex items-start gap-3">
        <ArrowDownTrayIcon className="mt-0.5 h-6 w-6 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-snug">Töltsd le a {appName} appot a telefonodra</p>
          <p className="mt-0.5 text-sm text-white/85">Egy koppintással indul, és az értesítések is biztosan megérkeznek.</p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Most nem"
          className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-[15px] font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-60"
      >
        <ArrowDownTrayIcon className="h-5 w-5" />
        {busy ? 'Telepítés...' : 'Letöltés'}
      </button>

      {showIosSteps && (
        <ol className="mt-3 space-y-2 rounded-xl bg-white/10 p-3 text-sm">
          <li className="flex items-start gap-2">
            <ArrowUpOnSquareIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <span>
              Koppints a <strong>Megosztás</strong> gombra a Safari alsó sávjában.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <PlusIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <span>
              Válaszd a <strong>Főképernyőhöz adás</strong> lehetőséget, majd a <strong>Hozzáadás</strong> gombot.
            </span>
          </li>
        </ol>
      )}
    </div>
  );
};

export default InstallAppBanner;
